import React, {useState, useRef, useEffect} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Link} from 'react-router-dom';
import {
    faHome,
    faTachometerAlt,
    faBell,
    faTools,
    faChevronUp,
    faChevronDown,
    faDatabase
} from '@fortawesome/free-solid-svg-icons';
import usePermissions from './hooks/usePermissions';
import './Sidebar.css';

type SidebarProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
};

const Sidebar: React.FC<SidebarProps> = ({isOpen, setIsOpen}) => {
    const [isSubMenuOpen, setSubMenuOpen] = useState<Record<string, boolean>>({});
    const {permissions} = usePermissions();
    const sidebarRef = useRef<HTMLDivElement>(null);

    const handleOutsideClick = (event: MouseEvent) => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen]);

    const handleToggleSubMenu = (menu: string): void => {
        setSubMenuOpen(prevState => ({
            ...prevState,
            [menu]: !prevState[menu]
        }));
    };

    const hasPermission = (requiredPermissions: string[]): boolean => {
        return requiredPermissions.some(permission => permissions.includes(permission));
    };

    const canViewPingLogs = hasPermission(['view_ping_logs']);

    const canViewDashboardsMenu = hasPermission([
        'view_dashboards',
        'create_dashboard',
        'view_dashboard',
        'view_ping_logs',
    ]);

    const canViewDataSourceMenu = hasPermission([
        'manage_prometheus_config',
    ]);

    const canViewAlertingMenu = hasPermission([
        'view_alerts',
        'manage_media',
        'manage_credentials',
        'manage_snmp',
        'configure_metrics',
        'edit_value_mapping',
        'manage_zabbix_data_sources',
        'configure_api_source',
        'manage_api_keys',
        'manage_http_gateway',
    ]);

    const canViewAdministrationMenu = hasPermission([
        'view_users',
        'create_users',
        'edit_users',
        'delete_users',
        'check_tenant_name',
        'create_tenants',
        'view_tenant',
        'edit_tenant',
        'delete_tenant',
        'list_tenants',
        'ip_whitelist',
    ]);

    return (
        <div ref={sidebarRef} className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
            <div className="menu-item">
                <FontAwesomeIcon icon={faHome}/>
                <Link to="/" className="sub-menu-link">Home</Link>
            </div>

            {canViewDashboardsMenu && (
                <div className="menu-item" onClick={() => handleToggleSubMenu('dashboards')}>
                    <FontAwesomeIcon icon={faTachometerAlt}/>
                    <span>Dashboards</span>
                    {isSubMenuOpen['dashboards'] ? (
                        <FontAwesomeIcon icon={faChevronUp}/>
                    ) : (
                        <FontAwesomeIcon icon={faChevronDown}/>
                    )}
                </div>
            )}

            {isSubMenuOpen['dashboards'] && canViewDashboardsMenu && (
                <div className="sub-menu">
                    {hasPermission(['create_dashboard']) &&
                        <Link to="/dashboard/create" className="sub-menu-link">Create Dashboard</Link>}
                    {hasPermission(['view_dashboard']) &&
                        <Link to="/dashboard" className="sub-menu-link">Dashboard Overview</Link>}
                    {canViewPingLogs &&
                        <Link to="/pinglog" className="sub-menu-link">ping log</Link>}
                </div>
            )}

            {canViewDataSourceMenu && (
                <div className="menu-item" onClick={() => handleToggleSubMenu('dataSource')}>
                    <FontAwesomeIcon icon={faDatabase}/>
                    <span>Data Source</span>
                    {isSubMenuOpen['dataSource'] ? (
                        <FontAwesomeIcon icon={faChevronUp}/>
                    ) : (
                        <FontAwesomeIcon icon={faChevronDown}/>
                    )}
                </div>
            )}

            {isSubMenuOpen['dataSource'] && canViewDataSourceMenu && (
                <div className="sub-menu">
                    <Link to="/data-source" className="sub-menu-link">Data Source</Link>
                </div>
            )}

            {canViewAlertingMenu && (
                <div className="menu-item" onClick={() => handleToggleSubMenu('alerts')}>
                    <FontAwesomeIcon icon={faBell}/>
                    <span>Alerting</span>
                    {isSubMenuOpen['alerts'] ? (
                        <FontAwesomeIcon icon={faChevronUp}/>
                    ) : (
                        <FontAwesomeIcon icon={faChevronDown}/>
                    )}
                </div>
            )}

            {isSubMenuOpen['alerts'] && canViewAlertingMenu && (
                <div className="sub-menu">
                    {hasPermission(['manage_media']) &&
                        <Link to="/alerts/media" className="sub-menu-link">Media</Link>}
                    {hasPermission(['manage_credentials']) &&
                        <Link to="/alerts/SnmpCredential" className="sub-menu-link">Snmp Credential</Link>}
                    {hasPermission(['manage_snmp']) &&
                        <Link to="/alerts/snmpoid-manager" className="sub-menu-link">Snmp OID Manager</Link>}
                    {hasPermission(['configure_metrics']) &&
                        <Link to="/alerts/probe-manager" className="sub-menu-link">Probe Config</Link>}
                    {hasPermission(['edit_value_mapping']) &&
                        <Link to="/alerts/value-mapping" className="sub-menu-link">Value Mapping</Link>}
                    {hasPermission(['configure_api_source']) &&
                        <Link to="/alerts/apisource-config" className="sub-menu-link">Api Source Config</Link>}
                    {hasPermission(['manage_api_keys']) && <Link to="/alerts/api-keys" className="sub-menu-link">
                        API Keys
                    </Link>}
                    {hasPermission(['manage_http_gateway']) &&
                        <Link to="/alerts/http-gateway" className="sub-menu-link">
                            HTTP Gateway
                        </Link>}
                    {hasPermission(['manage_zabbix_data_sources']) &&
                        <Link to="/alerts/zabbix-data-sources" className="sub-menu-link">Zabbix Data Sources</Link>
                    }
                    {hasPermission(['configure_metrics']) &&
                        <Link to="/alerts/metrics-config" className="sub-menu-link">Metric Config</Link>}
                </div>
            )}

            {canViewAdministrationMenu && (
                <div className="menu-item" onClick={() => handleToggleSubMenu('administration')}>
                    <FontAwesomeIcon icon={faTools}/>
                    <span>Administration</span>
                    {isSubMenuOpen['administration'] ? (
                        <FontAwesomeIcon icon={faChevronUp}/>
                    ) : (
                        <FontAwesomeIcon icon={faChevronDown}/>
                    )}
                </div>
            )}

            {isSubMenuOpen['administration'] && canViewAdministrationMenu && (
                <div className="sub-menu">
                    {hasPermission(['view_users']) &&
                        <Link to="/administration/users" className="sub-menu-link">Users</Link>}
                    {hasPermission(['ip_whitelist']) &&
                        <Link to="/administration/ip-whitelist" className="sub-menu-link">IP White List</Link>}
                    {hasPermission(['list_tenants']) && (
                        <Link to="/administration/tenant" className="sub-menu-link">Tenant Form</Link>
                    )}
                </div>
            )}
        </div>
    );
};

export default Sidebar;